// src/api/invoicingApi.ts
import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { logout } from '../reducers/authSlice';
import { stepUpRequired } from '../store/mfaSlice';
import { isMfaRequired } from './errors';
import { isSessionOver } from './axiosInstance';

/**
 * Dedicated axios instance for the owner-only invoicing screen.
 *
 * It does NOT reuse the shared `api` instance. When this was written that instance dispatched
 * `logout()` on any 403, and the invoicing endpoints answer 403 for reasons that are not "your
 * session expired" — e.g. an account that is `isAdmin` in the UI but lacks the backend
 * `c_role = 'ADMIN'`, or a denied continuation of an already-authorized request. Reusing it would
 * have signed the owner out of the whole site mid-sync/export.
 *
 * The shared instance has since learned the same body-code rule, so the two no longer disagree
 * about what a 403 means. This one stays because it is still narrower: it carries none of the
 * step-up traffic and so wants none of the `stepUpRequired()` dispatch, and its blob-bodied
 * downloads are the case the predicate below was written around. Same auth header, its own errors
 * on screen.
 *
 * None of that applies to a dead session, and a dead session is no longer distinguishable by status
 * alone: the API answers a missing, expired or invalid token with 403 too. `isSessionOver` reads
 * the error code the auth entry point puts in the body, so those clear the store while an
 * authorisation refusal still reaches the screen. It matters here more than anywhere: the export
 * and PDF downloads use `responseType: 'blob'`, so their error body is not a parsed object, and a
 * status-only check would have left the operator stuck on a screen whose every button fails.
 */
const invoicingApi = axios.create({
    baseURL: config.API_URL,
});

invoicingApi.interceptors.request.use(
    (requestConfig) => {
        const token = store.getState().auth.accessToken;
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    },
    (error) => Promise.reject(error)
);

invoicingApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Session over, never a plain authorisation 403. That distinction is the whole reason this
        // instance exists, and isSessionOver draws it from the body rather than the status so it
        // keeps holding when the server goes back to answering 401.
        if (await isSessionOver(error)) {
            store.dispatch(logout());
        } else if (isMfaRequired(error)) {
            // The same arm the shared instance and `securityApi` carry, and this screen needs it
            // most: with enforcement on, an admin who has not enrolled sees every panel here answer
            // `403 mfa_required` and — without this — nothing that says what to do about it. The one
            // modal host reads this and offers enrolment (or a code), instead of leaving the owner
            // on an invoicing page whose every button fails during the month's tax work. (Blob error
            // bodies are not decoded by `isMfaRequired`, so the CSV/ZIP export and the PDF download
            // do not raise it; the JSON list and stats calls on the same screen do, and they run
            // first.)
            store.dispatch(stepUpRequired());
        }
        return Promise.reject(error);
    }
);

export default invoicingApi;
